import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { model } from '@whizz/api-client';
import classnames from 'classnames';
import type StickerType from '@whizz/api-client/dist/@types/model/Sticker';
import type { ButtonProps } from '../../../components/button/types';
import type { ButtonHTMLAttributes } from 'react';
import type { StudentType } from '../../types';
import type { MaterialIconType } from '../../../util/types';
import { Button, ChoiceGroup, Collapse, Loader } from '../../../components';

type Props<T> = {
  open: boolean,
  selected: T,
  setSelected: (data: T) => void,
};

export const CommonButton = ({ children, icon, ...other }: ButtonProps & { icon?: MaterialIconType } & ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element => {
  return (
    <Button
      isOutline
      size={'sm'}
      color={'dark'}
      icon={icon}
      {...other}
    >
      {children as string}
    </Button>);
};

export const StudentPicker = ({ open, students, selected, setSelected }: Props<StudentType[]> & {
  students: StudentType[],
}): JSX.Element => {

  const { translate } = useI18n();
  const toggleSelectAll = () => setSelected(selected.length === students.length ? [] : students);
  const toggleStudent = (id: number) => () => {
    let selectedStudents = [ ...selected ];
    const find = selectedStudents.find((selectedStudent: StudentType) => selectedStudent.id === id);
    if (find) {
      selectedStudents = selectedStudents.filter((selectedStudent: StudentType) => selectedStudent.id !== id);
    } else {
      selectedStudents.push(students.find((selectedStudent: StudentType) => selectedStudent.id === id) as StudentType);
    }
    setSelected(selectedStudents);
  };

  return (
    <Collapse when={open}>
      <div className={'rounded bg-gradient-to-b from-transparent to-gray-200'}>
        <div className={'border-b-4 p-sm mb-sm'}>
          <ChoiceGroup
            id={'student-select-all'}
            label={translate('pages.HOME.MESSAGE_STUDENTS.SELECT_ALL')}
            checked={selected.length > 0}
            indeterminate={selected.length !== students.length}
            onChange={toggleSelectAll}
          />
        </div>
        <div className={'p-sm'}>
          {students.map((student, index) => {
            const { id, fullName } = student;
            return (
              <div key={id} className={classnames({ 'mb-sm': index !== students.length })}>
                <ChoiceGroup
                  id={id.toString()}
                  label={fullName}
                  checked={!!selected.find((selectedStudent: StudentType) => selectedStudent.id === student.id)}
                  onChange={toggleStudent(student.id)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Collapse>
  );
};

export const StickerPicker = ({ open = false, selected, setSelected }: Props<StickerType | null>): JSX.Element | null => {

  const { locale } = useI18n();
  const [ stickers, setStickers ] = useState<StickerType[] | null>(null);

  const pickStickerHandler = (id: StickerType | null) => () => {
    setSelected(id);
  };

  useEffect(() => {
    model.Sticker.all(locale)
      .then((stickers: StickerType[]) => setStickers(stickers));
  }, [ locale ]);

  const selectedSticker: StickerType | null = (stickers && selected) ? stickers.find((sticker: StickerType) => sticker.id === selected.id) as StickerType : null;

  return selectedSticker ? (
    (selectedSticker.url && selectedSticker.id) ? (
      <div className={'container flex justify-center bg-gray-100 p-sm rounded'}>
        <img
          className={'cursor-pointer w-32 h-32 md:w-40 md:h-40 m-1 p-0'}
          src={selectedSticker.url}
          alt={'Selected sticker'}
          onClick={pickStickerHandler(null)}
        />
      </div>
    ) : null
  ) : (
    <Collapse when={open}>
      {stickers ? (
        <div className={'container flex flex-wrap justify-center h-28 bg-gray-100 rounded overflow-y-auto p-sm'}>
          {
            stickers.map((sticker: StickerType) => {
              const { id, url } = sticker;
              return url && id ? (
                <img
                  key={id}
                  className={'w-16 h-16 cursor-pointer transition duration-150 ease-in-out transform hover:scale-110 m-1 p-0'}
                  src={url}
                  alt={`sticker-${sticker.id}`}
                  onClick={pickStickerHandler(sticker)}
                />
              ) : null;
            }) }
        </div>
      ) : <Loader />}
    </Collapse>
  );
};
